/**
 * Minimal RIFF/WAVE writer.
 *
 * The point of the whole app, arguably: the impulse response is a WAV file you
 * can drop into any convolution reverb and make anything sound like it was
 * recorded in that room.
 */

export type WavFormat = 'float32' | 'pcm24' | 'pcm16';

const FORMAT_PCM = 1;
const FORMAT_IEEE_FLOAT = 3;

export interface WavOptions {
  sampleRate: number;
  format?: WavFormat;
}

/**
 * Encode mono samples as a WAV file.
 *
 * float32 is the default because an impulse response is measurement data
 * before it is audio - it should survive a round trip without a quantisation
 * floor sitting under the reverb tail. Written with an 18-byte fmt chunk and
 * a fact chunk, which is what the spec asks for in non-PCM formats and what
 * the stricter hosts insist on.
 */
export function encodeWav(samples: ArrayLike<number>, options: WavOptions): ArrayBuffer {
  const { sampleRate, format = 'float32' } = options;
  const channels = 1;
  const bitsPerSample = format === 'float32' ? 32 : format === 'pcm24' ? 24 : 16;
  const bytesPerSample = bitsPerSample / 8;
  const audioFormat = format === 'float32' ? FORMAT_IEEE_FLOAT : FORMAT_PCM;
  const isFloat = format === 'float32';

  const dataBytes = samples.length * bytesPerSample * channels;
  const fmtChunkSize = isFloat ? 18 : 16;
  const factChunkBytes = isFloat ? 12 : 0;
  const headerBytes = 12 + (8 + fmtChunkSize) + factChunkBytes + 8;

  const buffer = new ArrayBuffer(headerBytes + dataBytes);
  const view = new DataView(buffer);
  let offset = 0;

  const writeAscii = (text: string): void => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset++, text.charCodeAt(i));
  };
  const writeU32 = (value: number): void => { view.setUint32(offset, value, true); offset += 4; };
  const writeU16 = (value: number): void => { view.setUint16(offset, value, true); offset += 2; };

  writeAscii('RIFF');
  writeU32(headerBytes + dataBytes - 8);
  writeAscii('WAVE');

  writeAscii('fmt ');
  writeU32(fmtChunkSize);
  writeU16(audioFormat);
  writeU16(channels);
  writeU32(sampleRate);
  writeU32(sampleRate * channels * bytesPerSample);
  writeU16(channels * bytesPerSample);
  writeU16(bitsPerSample);
  if (isFloat) writeU16(0); // cbSize, extension length

  if (isFloat) {
    writeAscii('fact');
    writeU32(4);
    writeU32(samples.length);
  }

  writeAscii('data');
  writeU32(dataBytes);

  if (format === 'float32') {
    for (let i = 0; i < samples.length; i++) {
      view.setFloat32(offset, samples[i], true);
      offset += 4;
    }
  } else if (format === 'pcm24') {
    for (let i = 0; i < samples.length; i++) {
      const v = clampToInt(samples[i], 8388607);
      view.setUint8(offset++, v & 0xff);
      view.setUint8(offset++, (v >> 8) & 0xff);
      view.setUint8(offset++, (v >> 16) & 0xff);
    }
  } else {
    for (let i = 0; i < samples.length; i++) {
      view.setInt16(offset, clampToInt(samples[i], 32767), true);
      offset += 2;
    }
  }

  return buffer;
}

/** Symmetric clamp before rounding, so a sample at exactly 1.0 does not wrap. */
function clampToInt(sample: number, peak: number): number {
  const scaled = Math.round(sample * peak);
  return scaled > peak ? peak : scaled < -peak - 1 ? -peak - 1 : scaled;
}

export function wavBlob(samples: ArrayLike<number>, options: WavOptions): Blob {
  return new Blob([encodeWav(samples, options)], { type: 'audio/wav' });
}
